/* eslint-disable max-lines */
import {
  ArrowDownTrayIcon,
  ArrowPathIcon,
  EyeIcon,
  LinkIcon
} from "@heroicons/react/24/outline";
import cx from "classnames";
import { useState } from "react";
import { Link } from "react-router-dom";
import toast from "react-hot-toast";
import Can from "../../../components/users/permissions/Can";
import SpinnerLoader from "../../../components/ui/SpinnerLoader";
import Table, { Row } from "../../../components/ui/table";
import Pagination from "../../../components/ui/table/Pagination";
import { useSearch } from "../../../hooks/useSearch";
import { IGTFSValidationResult } from "../../../interfaces/endpoints/IGTFS.types";
import { getValidations, triggerGenerateGTFSFeed } from "../../../services/GTFS.service";
import { getFeedUrl } from "../../../utils/gtfsFeedUrl";

const COLUMNS = [
  "ID",
  "Feed Version",
  "Valid",
  "Errors",
  "Warnings",
  "Agencies",
  "Routes",
  "Trips",
  "Stops",
  "Feed URL",
  "Generated At",
  "Actions"
];

const PAGE_SIZE = 10;

const EXTERNAL_VALIDATOR_URL = "https://gtfs-validator.mobilitydata.org/";
const UPLOAD_TO_TRANSIT_URL =
  "https://transitpartners.webapps.google.com/static_pipeline_details/sbcfv7776rrnd5?a=3002845420293455873";

const buttonClassName = cx([
  "inline-flex justify-center items-center gap-2 px-4 py-2",
  "font-bold duration-300 text-sm",
  "bg-primary text-white rounded-lg hover:bg-primary-dark hover:shadow-xl"
]);

const GTFSFeeds: React.FC = () => {
  const [isGenerating, setIsGenerating] = useState(false);
  const { response, loading, handlePageChange, page, refetch } = useSearch(
    getValidations,
    { otherQuery: { pageSize: String(PAGE_SIZE) } }
  );

  const handleGenerateFeed = async () => {
    setIsGenerating(true);
    try {
      const res = await triggerGenerateGTFSFeed();
      if (res.status === 202 || res.status === 200) {
        toast.success(
          "Gjenerimi u nis. Mund të largoheni nga faqja - feed do të gjenerohet në sfond."
        );
        setTimeout(() => refetch(), 2000);
      } else {
        toast.error((res as any)?.message ?? "Gjenerimi dështoi");
      }
    } catch (error) {
      console.error("GTFS feed generation trigger failed", error);
      toast.error("Gjenerimi i GTFS feed dështoi");
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <>
      <div className="mb-4">
        <h2 className="text-lg font-semibold">Feeds</h2>
      </div>
      <div className="mb-4 flex flex-wrap gap-2">
        <Can permissions="gtfs.create">
          <button
            type="button"
            onClick={handleGenerateFeed}
            disabled={isGenerating}
            className={buttonClassName}
          >
            {isGenerating ? (
              <>
                <SpinnerLoader size={16} />
                Duke nisur...
              </>
            ) : (
              <>
                <ArrowPathIcon className="w-4 h-4" />
                Gjenero Feed
              </>
            )}
          </button>
        </Can>
        <a
          href={EXTERNAL_VALIDATOR_URL}
          target="_blank"
          rel="noopener noreferrer"
          className={cx(buttonClassName, "!bg-gray-600 hover:!bg-gray-700")}
        >
          <LinkIcon className="w-4 h-4" />
          Valido Feed (eksternal)
        </a>
        <a
          href={UPLOAD_TO_TRANSIT_URL}
          target="_blank"
          rel="noopener noreferrer"
          className={cx(buttonClassName, "!bg-gray-600 hover:!bg-gray-700")}
        >
          <ArrowDownTrayIcon className="w-4 h-4" />
          Ngarko në Transit
        </a>
      </div>
      <div className="flex flex-col" style={{ height: "calc(100vh - 15rem)" }}>
        <Table titles={COLUMNS} style="hover" loading={loading}>
          {!loading && response?.data?.result?.length ? (
            response.data.result.map((row: IGTFSValidationResult) => (
              <Row
                key={row.id}
                id={row.id}
                feedVersion={row.feedVersion}
                valid={
                  <span
                    className={`px-2 py-1 rounded-full text-xs font-medium ${
                      row.isValid
                        ? "bg-green-100 text-green-800"
                        : "bg-red-100 text-red-800"
                    }`}
                  >
                    {row.isValid ? "Valid" : "Invalid"}
                  </span>
                }
                errors={row.errorCount}
                warnings={row.warningCount}
                agencies={row.totalAgencies}
                routes={row.totalRoutes}
                trips={row.totalTrips}
                stops={row.totalStops}
                feedUrl={
                  <a
                    href={getFeedUrl(row)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary hover:underline text-sm break-all max-w-[200px] truncate block"
                    title={getFeedUrl(row)}
                  >
                    {getFeedUrl(row)}
                  </a>
                }
                generatedAt={new Date(row.generatedAt).toLocaleString()}
                actions={
                  <Link
                    className="p-2.5 md:p-1"
                    state={{ crumbs: ["Menaxhimi i GTFS", "Feeds", "Raport"] }}
                    to={`/gtfs/feeds/validations/${row.id}`}
                  >
                    <EyeIcon className="w-5 h-5 md:w-4" />
                  </Link>
                }
              />
            ))
          ) : (
            <tr>
              <td colSpan={COLUMNS.length} className="text-center py-4">
                {loading ? "Loading..." : "Nuk u gjetën rezultate validimi"}
              </td>
            </tr>
          )}
        </Table>
        <Pagination
          onPageChange={handlePageChange}
          totalCount={response?.data?.totalCount || 0}
          length={response?.data?.result?.length || 0}
          page={page}
          pageSize={PAGE_SIZE}
        />
      </div>
    </>
  );
};

export default GTFSFeeds;
